/**
 * 「商家在聊天里要求发布」这条指令的兜底执行者 —— 没有 UI,挂在 `DashboardLayout` 里。
 *
 * 为什么挂在布局而不是页面:发布指令是一条**必须恰好执行一次的持久命令**,而在这之前接它的
 * 代码是 `ContentStudioPage` 的一个 `useEffect`,依赖当前活跃会话正在流的那个 turn 的工具列表。
 * 商家一切走页面(或切到别的会话),就没人接了 —— 不发、不报错、Maya 干等。dev 实测:
 * 要求发布后 5 秒切到 Marketing Hub,turn 3 秒后在别处结束,`published_content` 纹丝不动。
 * 详细的根因与证据在 `publishSignal.ts` 的顶注。
 *
 * `DashboardLayout` 包住 `/` 下全部已登录路由,所以站内怎么导航这个订阅都活着。
 *
 * 两条纪律:
 *
 * ① **不要在这里用 `useWsChat`** —— 它会 `retain()`,而 `retain/release` 是 socket 空闲关闭的
 *    唯一计数来源(`useWsChat.ts` 是唯一调用方)。常驻 retain 会让 socket 永不 idle-close。
 *    这里只订阅 manager,不持有连接。
 *
 * ② **不要 import `ContentStudioPage`** —— 它是路由级 lazy chunk(半兆),从常驻布局里引它会把
 *    整个页面拖进主包。所以稿件不从聊天记录里解析,而是从已落库的草稿行读(那也是更准的正文:
 *    服务端改写过的链接和 Hub 的编辑都在那一行上)。
 *
 * 在 `subscribeTurnComplete` 上触发:工具的 docstring 要求 Maya 调完就 WAIT,所以 turn 结束正是
 * 「她已停下、在等前端」的确切时刻;而这时工具已经归档进 traces,读得到。页面在场时它自己那条
 * 更快的路径会先认领,这里就自然跳过 —— 两边共用 `claimPublishSignal`,保证恰好一次。
 */
import { useEffect } from 'react';
import { useAuthStore } from '@stores/authStore';
import { wsChatManager } from '@lib/services/agents/ws-chat/manager';
import {
  claimPublishSignal,
  executePublishSignal,
  findPublishSignalInTraces,
} from '@lib/services/agents/publishSignal';

const MARKETING = 'marketing';

export function PublishSignalExecutor() {
  const userId = useAuthStore(s => s.user?.id);

  useEffect(() => {
    if (!userId) return;
    return wsChatManager.subscribeTurnComplete(sessionId => {
      // 只有 Maya 有这条通路;别的 agent 的 turn 结束不该在这里做任何事。
      if (wsChatManager.getSessionAgentType(sessionId) !== MARKETING) return;
      const session = wsChatManager.getSession(sessionId);
      if (!session) return;
      const signal = findPublishSignalInTraces(session.state.traces);
      if (!signal) return;
      // 页面在场时它已经认领过了 —— 这里静默跳过,不是错误路径。
      if (!claimPublishSignal(signal.callId)) return;

      void executePublishSignal({ userId, signal })
        .then(({ message }) => {
          // 结果必须回到会话里:Maya 在等这句话才能收尾,商家也才知道发生了什么。
          // 即使一篇都没发出去也要说 —— 静默正是这个 bug 本来的样子。
          void wsChatManager.sendMessage(sessionId, message, MARKETING);
        })
        .catch(err => {
          console.warn('[PublishSignalExecutor] publish failed', err);
        });
    });
  }, [userId]);

  return null;
}
